import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import api from '../utils/api';

const SurveyPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [questions, setQuestions] = useState([]);
  const [loadingQ, setLoadingQ] = useState(true);
  const [formData, setFormData] = useState({ user_name: '', instansi: '', saran_pengguna: '' });
  const [answers, setAnswers] = useState({});   // { [question_id]: score }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/surveys/questions')
      .then(r => {
        setQuestions(r.data);
        setLoadingQ(false);
      })
      .catch(() => { toast.error('Gagal memuat pertanyaan survei.'); setLoadingQ(false); });
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleStar = (qid, score) => setAnswers({ ...answers, [qid]: score });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_name) { toast.error('Nama lengkap wajib diisi.'); return; }
    for (const q of questions) {
      if (!answers[q.id]) { toast.error(`Harap beri penilaian untuk pertanyaan ${q.order}.`); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/surveys', { ...formData, answers });
      toast.success(data.message);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (qid) => (
    <div style={{ display: 'flex', gap: '10px', marginTop: 8 }}>
      {[1, 2, 3, 4, 5].map(star => {
        const selected = answers[qid] >= star;
        const hovered = false;
        return (
          <button
            key={star}
            type="button"
            onClick={() => handleStar(qid, star)}
            title={['', 'Sangat Buruk', 'Buruk', 'Cukup', 'Baik', 'Sangat Baik'][star]}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: '2.25rem', lineHeight: 1,
              color: selected ? '#f59e0b' : (isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'),
              textShadow: selected ? '0 0 12px rgba(245,158,11,0.5)' : 'none',
              transform: selected ? 'scale(1.15)' : 'scale(1)',
              transition: 'all 0.15s ease',
            }}
          >★</button>
        );
      })}
      {answers[qid] && (
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>
          {['', 'Sangat Buruk', 'Buruk', 'Cukup', 'Baik', 'Sangat Baik'][answers[qid]]}
        </span>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 24px 40px' }}>
      <div className="card" style={{ maxWidth: 680, width: '100%', position: 'relative', zIndex: 10 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <img src="/logo_ngada.png" alt="Logo Kab Ngada" style={{ width: 56, height: 56, objectFit: 'contain' }} />
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(59,130,246,0.12)',
            border: '1px solid rgba(59,130,246,0.3)', borderRadius: 9999, padding: '5px 14px',
            fontSize: '0.75rem', fontWeight: 700, color: 'var(--info)', letterSpacing: '0.05em', marginBottom: 16
          }}>📋 SURVEI KEPUASAN MASYARAKAT</div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: 8, color: 'var(--text-primary)' }}>
            Survey Kepuasan Layanan LPSE
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', maxWidth: 500, margin: '0 auto' }}>
            Pendapat Anda sangat berharga untuk peningkatan layanan Pengadaan Barang &amp; Jasa Kabupaten Ngada.
          </p>
        </div>

        {loadingQ ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            Memuat pertanyaan survei...
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            {/* Identitas */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div className="form-group" style={{ flex: '1 1 250px' }}>
                <label className="form-label">Nama Lengkap <span style={{ color: 'var(--error)' }}>*</span></label>
                <input type="text" name="user_name" value={formData.user_name}
                  onChange={handleChange} className="form-input" placeholder="Nama Anda" required />
              </div>
              <div className="form-group" style={{ flex: '1 1 250px' }}>
                <label className="form-label">Instansi / Perusahaan</label>
                <input type="text" name="instansi" value={formData.instansi}
                  onChange={handleChange} className="form-input" placeholder="Opsional" />
              </div>
            </div>

            {/* Pertanyaan Dinamis */}
            {questions.length === 0 ? (
              <div style={{ padding: '24px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Belum ada pertanyaan aktif dari admin.
              </div>
            ) : (
              <>
                <div style={{ borderTop: '1px solid var(--border-secondary)' }} />
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: -12 }}>
                  Berikan penilaian bintang untuk setiap aspek layanan berikut:
                </p>
                {questions.map((q, idx) => (
                  <div key={q.id} style={{
                    background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)',
                    padding: '20px', border: `1px solid ${answers[q.id] ? 'rgba(245,158,11,0.3)' : 'var(--border-secondary)'}`
                  }}>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                      <span style={{ color: 'var(--accent-primary)', marginRight: 6 }}>{idx + 1}.</span>
                      {q.question_text}
                    </p>
                    {q.description && (
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', marginBottom: 4 }}>{q.description}</p>
                    )}
                    {renderStars(q.id)}
                  </div>
                ))}
              </>
            )}

            {/* Saran */}
            <div className="form-group">
              <label className="form-label">Kritik &amp; Saran</label>
              <textarea name="saran_pengguna" value={formData.saran_pengguna}
                onChange={handleChange} className="form-textarea" rows={4}
                placeholder="Tuliskan saran, masukan, atau kritik Anda untuk LPSE Kabupaten Ngada (opsional)..." />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary" style={{ flex: 1 }}>
                Batal
              </button>
              <button type="submit" disabled={submitting || questions.length === 0} className="btn btn-primary" style={{ flex: 2 }}>
                {submitting ? 'Mengirim...' : '📤 Kirim Survei'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SurveyPage;
